import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Lightbox, LightboxAuthor, photoCaptionChars } from "@/components/PhotoGallery";
import { VideoPlayer } from "@/components/VideoPlayer";
import { ReactionBar } from "@/components/ReactionBar";
import { AttnPulse } from "@/components/AttnPulse";
import { CommentModal } from "@/components/CommentModal";
import { AdStageCta } from "@/components/AdStageCta";
import { GuestCta } from "@/components/GuestCta";
import { PostLiveReactions } from "@/components/PostLiveReactions";
import { EmbedPlayer } from "@/components/EmbedPlayer";
import { postEmbedOf } from "@/lib/postMedia";

// Mobile video / replay (and video ad) viewer — same shell as the photo viewer: fullscreen black
// stage, author + text (« Voir plus ») + J'aime / Commenter / Partager at the bottom, Reels-style
// comments sheet. The player is ONE node: it keeps playing while the stage shrinks into the top band.
// `initialComments` / `focusCommentId`: deep link from a notification (?focus=comments&comment=…).
export const VideoViewerMobile = ({ post, onClose, initialComments = false, focusCommentId = null, focusReactions = false }) => {
  const { t, config, user } = useApp();
  const [comments, setComments] = useState(initialComments);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const src = post.hls_url || post.video_url;
  // Vidéo par lien (YouTube / Facebook) : lecteur intégré avec le son, même scène noire que les vidéos Yalola.
  const embed = src ? null : postEmbedOf(post);
  if (!src && !embed) return null;
  return (
    <>
    <PostLiveReactions post={post} />
    <Lightbox
      images={[src || embed.url || embed.src]}
      index={0}
      onIndex={() => {}}
      onClose={onClose}
      stage={embed ? (
        <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()} data-testid="video-viewer-stage">
          <div className="w-full max-h-full px-0" data-testid={`video-viewer-embed-${post.id}`}>
            <EmbedPlayer embed={embed} preview={false} sound className="rounded-none" testId={`video-viewer-embed-player-${post.id}`} />
            {(post.embed?.title || post.embed?.author) && (
              <div className="px-4 pt-3 text-white/85" data-testid={`video-viewer-embed-meta-${post.id}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-white/55">{embed.provider === "youtube" ? "YOUTUBE.COM" : "FACEBOOK.COM"}</div>
                {post.embed?.title && <div className="text-sm font-semibold line-clamp-2">{post.embed.title}</div>}
                {post.embed?.author && <div className="text-xs text-white/60 truncate">{post.embed.author}</div>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()} data-testid="video-viewer-stage">
          <VideoPlayer mode="controls" src={src} poster={post.poster_url || undefined} fx={post.video_fx || null} autoPlay initialProcessing={!!post.video_processing} adaptRatio maxHeight="100%" className="w-full rounded-lg" testId="video-viewer-player" resumeKey={post.id} resumeAt={post.resume_t || 0} fullscreenButton speedControl>
            {post.is_replay && (
              <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white pointer-events-none"
                style={{ background: "rgba(217,87,87,0.94)", backdropFilter: "blur(4px)" }} data-testid="video-viewer-replay-badge">
                {t("post.replay_badge", "Replay")}
              </div>
            )}
          </VideoPlayer>
        </div>
      )}
      author={<LightboxAuthor post={post} />}
      caption={post.content || ""}
      shareLink={`/post/${post.id}`}
      downloadUrl={embed ? null : (post.video_url || null)}
      downloadKind="video"
      maxChars={photoCaptionChars(config)}
      actions={(
        <>
          {post.ad && <AdStageCta ad={post.ad} author={post.author} variant="card" />}
          {!user && <GuestCta compact dark testId="guest-cta-viewer" />}
          <AttnPulse active={focusReactions} testId={`viewer-reactions-${post.id}`}>
            <ReactionBar dark post={post} commentCount={commentCount} onCommentClick={() => setComments(true)} autoOpenReactors={focusReactions} />
          </AttnPulse>
        </>
      )}
      comments={comments ? (
        <CommentModal post={post} open embedded focusCommentId={focusCommentId} onOpenChange={(o) => { if (!o) setComments(false); }} onCountChange={(d) => setCommentCount((c) => Math.max(0, c + d))} />
      ) : null}
      onCommentsClose={() => setComments(false)}
    />
    </>
  );
};

export default VideoViewerMobile;
